// @ts-nocheck
import React, { useState } from 'react';
import { 
  Checkbox, 
  Select, 
  Tag, 
  Tooltip, 
  Card, 
  Collapse,
  Button
} from 'antd';
import { 
  InfoCircleOutlined, 
  LockOutlined, 
  ExclamationCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons';
import { colors, spacing } from '../design-system/tokens';
import { Typography } from '../design-system/typography';
import { 
  Snippet, 
  SnippetSelection, 
  SnippetVariation,
  ShipmentContext 
} from './SmartSnippetBuilder';

const { Option } = Select;
const { Panel } = Collapse;

interface SnippetItemProps {
  snippet: Snippet;
  selection: SnippetSelection;
  onChange: (changes: Partial<SnippetSelection>) => void;
  shipmentContext: ShipmentContext;
}

export const SnippetItem: React.FC<SnippetItemProps> = ({
  snippet,
  selection,
  onChange,
  shipmentContext
}) => {
  const [showPreview, setShowPreview] = useState(false);

  // Determine snippet state
  const isRequired = snippet.required || snippet.id.includes('required');
  const isRecommended = !isRequired && getRecommendationReason(snippet, shipmentContext);
  const isOptional = !isRequired && !isRecommended;
  const isUserModified = selection.userModified;

  // Get the text to display (variation or main text)
  const displayText = getDisplayText(snippet, selection);

  // Get triggering reason
  const triggerReason = getTriggerReason(snippet, shipmentContext);

  return (
    <Card
      size="small"
      style={{
        backgroundColor: selection.enabled ? 
          `${getSnippetColor(snippet)}10` : 
          colors.background.primary,
        border: `1px solid ${selection.enabled ? 
          getSnippetColor(snippet) : 
          colors.border.default}`,
        borderRadius: '6px',
        transition: 'all 0.2s ease'
      }}
      bodyStyle={{ padding: spacing.sm }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        {/* Header Row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.sm }}>
          {/* Checkbox */}
          <Checkbox
            checked={selection.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            disabled={isRequired}
            style={{ marginTop: '2px' }}
          />

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title Row */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: spacing.xs,
              marginBottom: spacing.xs
            }}>
              {/* Status indicators */}
              {isRequired && (
                <Tag 
                  icon={<LockOutlined />} 
                  color="red" 
                  style={{ fontSize: '10px', margin: 0 }}
                >
                  REQUIRED
                </Tag>
              )}
              
              {isRecommended && (
                <Tag 
                  color="orange" 
                  style={{ fontSize: '10px', margin: 0 }}
                >
                  RECOMMENDED
                </Tag>
              )}

              {isUserModified && (
                <Tag 
                  color="blue" 
                  style={{ fontSize: '10px', margin: 0 }}
                >
                  MODIFIED
                </Tag>
              )}

              {/* Source type indicator */}
              <Tag 
                color={getSourceColor(snippet.sourceType)}
                style={{ fontSize: '10px', margin: 0 }}
              >
                {snippet.sourceType.toUpperCase()}
              </Tag>

              {/* Info tooltip */}
              <Tooltip
                title={
                  <div>
                    <div><strong>Snippet ID:</strong> {snippet.id}</div>
                    <div><strong>Priority:</strong> {snippet.priority}</div>
                    {triggerReason && <div><strong>Triggered by:</strong> {triggerReason}</div>}
                    {snippet.metadata?.attributionText && 
                      <div><strong>Attribution:</strong> {snippet.metadata.attributionText}</div>
                    }
                    {snippet.metadata?.feeAmount && 
                      <div><strong>Fee Amount:</strong> {snippet.metadata.feeAmount}</div>
                    }
                  </div>
                }
                placement="right"
                overlayStyle={{ maxWidth: '300px' }}
              >
                <InfoCircleOutlined 
                  style={{ 
                    color: colors.text.tertiary, 
                    fontSize: '12px',
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </div>

            {/* Variation Selection */}
            {snippet.variations && snippet.variations.length > 0 && (
              <div style={{ marginBottom: spacing.sm }}>
                <Select
                  size="small"
                  value={selection.selectedVariationId || 'default'}
                  onChange={(value) => onChange({ 
                    selectedVariationId: value === 'default' ? undefined : value 
                  })}
                  style={{ width: '100%', fontSize: '12px' }}
                  disabled={!selection.enabled}
                >
                  <Option value="default">Default Version</Option>
                  {snippet.variations.map(variation => (
                    <Option key={variation.id} value={variation.id}>
                      {variation.name}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            {/* Text Preview */}
            <div style={{
              backgroundColor: colors.background.tertiary,
              padding: spacing.sm,
              borderRadius: '4px',
              border: `1px solid ${colors.border.default}`,
              maxHeight: showPreview ? 'none' : '60px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <Typography 
                variant="value" 
                style={{ 
                  fontSize: '11px',
                  lineHeight: '1.4',
                  color: selection.enabled ? colors.text.primary : colors.text.secondary,
                  fontFamily: 'Reddit Sans, sans-serif'
                }}
              >
                {displayText}
              </Typography>
              
              {!showPreview && displayText.length > 150 && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: `linear-gradient(transparent, ${colors.background.tertiary})`,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center'
                }}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => setShowPreview(true)}
                    style={{ fontSize: '10px', height: 'auto', padding: '2px 6px' }}
                  >
                    Show More
                  </Button>
                </div>
              )}
              
              {showPreview && displayText.length > 150 && (
                <div style={{ textAlign: 'center', marginTop: spacing.xs }}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeInvisibleOutlined />}
                    onClick={() => setShowPreview(false)}
                    style={{ fontSize: '10px', height: 'auto', padding: '2px 6px' }}
                  >
                    Show Less
                  </Button>
                </div>
              )}
            </div>

            {/* Character count and warnings */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: spacing.xs
            }}>
              <Typography 
                variant="value" 
                style={{ 
                  fontSize: '10px', 
                  color: colors.text.tertiary 
                }}
              >
                {displayText.length} chars
              </Typography>
              
              {selection.enabled && displayText.length > 500 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                  <ExclamationCircleOutlined 
                    style={{ 
                      color: colors.status.warning, 
                      fontSize: '10px' 
                    }} 
                  />
                  <Typography 
                    variant="value" 
                    style={{ 
                      fontSize: '10px', 
                      color: colors.status.warning 
                    }}
                  >
                    Long clause
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Get display text (variation or main text)
 */
function getDisplayText(snippet: Snippet, selection: SnippetSelection): string {
  if (selection.selectedVariationId && snippet.variations) {
    const variation = snippet.variations.find(v => v.id === selection.selectedVariationId);
    if (variation) {
      return variation.text;
    }
  }
  return snippet.text;
}

/**
 * Get snippet color based on category
 */
function getSnippetColor(snippet: Snippet): string {
  const colorMap: Record<string, string> = {
    'inspection': colors.status.positive,
    'regulatory': colors.status.negative,
    'warning': colors.status.warning,
    'terminal': colors.primary[60],
    'fee': colors.tertiary,
    'appointment': colors.neutral[40],
    'photo': colors.primary[40],
    'marking': colors.secondary,
    'other': colors.text.secondary
  };
  
  return colorMap[snippet.category] || colors.text.secondary;
}

/**
 * Get source type color
 */
function getSourceColor(sourceType: string): string {
  const colorMap: Record<string, string> = {
    'buyer': 'blue',
    'supplier': 'green',
    'carrier': 'purple',
    'commodity': 'orange',
    'regulatory': 'red'
  };
  
  return colorMap[sourceType] || 'default';
}

/**
 * Get recommendation reason
 */
function getRecommendationReason(snippet: Snippet, context: ShipmentContext): string | null {
  // Check if this snippet is recommended based on context
  
  // Port congestion warnings are recommended for most shipments
  if (snippet.id === 'port-congestion-standard') {
    return 'Recommended for all shipments due to ongoing port congestion';
  }
  
  // Photo requirements recommended for certain buyers
  if (snippet.id === 'photos-basic' && 
      ['DAH CHONG HONG', 'ISHIHARA'].some(buyer => 
        context.buyer?.name?.includes(buyer)
      )) {
    return 'Recommended based on buyer requirements';
  }
  
  // AES info recommended for exports
  if (snippet.id === 'aes-info-request') {
    return 'Recommended for export shipments';
  }
  
  return null;
}

/**
 * Get trigger reason for display
 */
function getTriggerReason(snippet: Snippet, context: ShipmentContext): string | null {
  const reasons: string[] = [];
  
  if (snippet.trigger.buyer && context.buyer?.name &&
      snippet.trigger.buyer.includes(context.buyer.name)) {
    reasons.push(`Buyer: ${context.buyer.name}`);
  }
  
  if (snippet.trigger.supplier && context.supplier?.name &&
      snippet.trigger.supplier.includes(context.supplier.name)) {
    reasons.push(`Supplier: ${context.supplier.name}`);
  }
  
  if (snippet.trigger.carrier && context.carrier?.name &&
      snippet.trigger.carrier.includes(context.carrier.name)) {
    reasons.push(`Carrier: ${context.carrier.name}`);
  }
  
  if (snippet.trigger.commodity && context.commodities) {
    const matchingCommodities = context.commodities.filter(c =>
      snippet.trigger.commodity!.includes(c.name)
    );
    if (matchingCommodities.length > 0) {
      reasons.push(`Commodities: ${matchingCommodities.map(c => c.name).join(', ')}`);
    }
  }
  
  return reasons.length > 0 ? reasons.join(' • ') : null;
}